import React, { useMemo } from "react";
import { Box, Icon, type IconName, Text } from "@salestrak/ui";
import type { ActivityNode, ActivityChange } from "./activity-types";
import { lifecycleVerb } from "./activity-types";
import { AGE_STATUS } from "#app/lib/age-status";
import { formatTime } from "./activity-utils";
import { EntityTag } from "#app/components/entity";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { ActionChunk } from "./chunks";
import { at } from "#app/lib/utils/at";

interface BaseItemProps {
  activity: ActivityNode;
  issueId: string;
  last: boolean;
}

// Change types that always show the user avatar (person-centric actions)
const PERSON_CHANGE_TYPES = new Set(["task", "assignees"]);

// Icon for each change type (stageId handled separately via the stage EntityTag)
const CHANGE_ICONS: Record<string, IconName> = {
  values: "box",
  form: "clipboard",
  note: "message-square",
  noteReply: "reply",
  name: "pen-line",
  age: "clock",
  issueTypeId: "tag",
  address: "map-pin",
  workflowId: ENTITY_KIND_ICON.workflow,
  trashed: "trash-2",
  archived: "archive",
  restored: "rotate-ccw",
  unarchived: "archive-restore",
};

const ICON_LANE_WIDTH = 20;
const GLYPH_BOX_SIZE = 16;
const SM_LINE_HEIGHT = 20; // text-sm line height — icon lane height pegs icon to first line
const CONNECTOR_TOP_OFFSET = SM_LINE_HEIGHT + 4; // icon lane bottom + 4px gap
const CONNECTOR_BOTTOM_OVERFLOW = 14; // reaches into the marginBottom gap to connect rows
const ICON_CENTER_X = 9.5;

export const BaseItem = React.memo(function BaseItem({ activity, last }: BaseItemProps) {
  const actor = activity.author;

  const actorName = useMemo(() => {
    if (actor?.name) return actor.name;
    const triggeredBy = activity.changeset?.age?.after?.triggeredBy;
    if (triggeredBy === "expired" || triggeredBy === "stageChanged")
      return "System";
    return "Unknown";
  }, [actor, activity.changeset]);

  const changes = useMemo(
    // Drop changes whose chunk would render nothing (assignee/field edits that
    // resolved to no entities) so a grouped line never trails a dangling
    // " and " connector or an actor-only row.
    () => processChangeset(activity).filter(changeRenders),
    [activity],
  );

  if (changes.length === 0) return null;

  // A server activity groups several changes (e.g. a stage move + assignee add)
  // in one changeset — render them as ONE row, not one row per change. The left
  // glyph represents the first change; the text joins every change's chunk.
  const primaryType = at(changes, 0, "change").target.type;
  const showAvatar = PERSON_CHANGE_TYPES.has(primaryType);
  const iconName = CHANGE_ICONS[primaryType];

  return (
    <Box
      marginBottom="md" direction="row"
      gap="sm"
      align="start"
      position="relative"
      className="opacity-95"
    >
      {!last && (
        <Box
          background="border" width={1}
          position="absolute"
          top={CONNECTOR_TOP_OFFSET}
          bottom={-CONNECTOR_BOTTOM_OVERFLOW}
          left={ICON_CENTER_X}
        />
      )}
      <Box
        align="center"
        justify="center"
        width={ICON_LANE_WIDTH}
        height={SM_LINE_HEIGHT}
      >
        <Box
          align="center"
          justify="center"
          width={GLYPH_BOX_SIZE}
          height={GLYPH_BOX_SIZE}
        >
          {showAvatar ? (
            // size="sm" matches the stage glyph's footprint (both 20px); no
            // overflow-clip wrapper — that cropped the avatar down to 16px and
            // made it read smaller than the icon/stage beside it.
            <EntityTag
              entity={{
                kind: "user",
                id: actor?.userId ?? "unknown",
                user: actor ?? null,
              }}
              display="visual"
              size="sm"
              interactive={false}
            />
          ) : primaryType === "stageId" ? (
            <EntityTag
              entity={{
                kind: "stage",
                id: activity.newStage?.stageId ?? "unknown",
                stage: activity.newStage ?? {},
                workflowId: activity.newStage?.workflowId ?? undefined,
              }}
              display="visual"
              size="sm"
              interactive={false}
            />
          ) : iconName ? (
            <Icon
              name={iconName}
              size="md"
              color={primaryType === "workflowId" ? "success" : "muted"}
            />
          ) : (
            <EntityTag
              entity={{
                kind: "user",
                id: actor?.userId ?? "unknown",
                user: actor ?? null,
              }}
              display="visual"
              size="sm"
              interactive={false}
            />
          )}
        </Box>
      </Box>

      <Box flex={1}>
        <Text size="sm" variant="muted">
          {actor?.userId ? (
            <EntityTag
              entity={{ kind: "user", id: actor.userId, user: actor }}
              display="label"
              weight="semibold"
              as="text"
            />
          ) : (
            <Text weight="semibold" size="sm">{actorName}</Text>
          )}
          <Text size="sm"> </Text>
          {changes.map((change, index) => {
            // First chunk has no connector; the last is joined with " and ",
            // any middle ones with ", " ("moved … , edited … and added …").
            const connector =
              index === 0
                ? null
                : index === changes.length - 1
                  ? " and "
                  : ", ";
            return (
              <Text size="sm" key={`${change.target.type}-${index}`}>
                {connector ? <Text size="sm">{connector}</Text> : null}
                <ActionChunk change={change} />
              </Text>
            );
          })}
          <Text size="sm" variant="muted"> · {formatTime(activity.lastUpdateDate ?? activity.creationDate)}</Text>
        </Text>
      </Box>
    </Box>
  );
});

/**
 * Whether a change resolves to visible text. The assignee and custom-field
 * chunks render null when their entity/field lists come back empty (e.g. the
 * backend omitted `fields`), so those changes must be dropped before joining —
 * every other change type always renders.
 */
function changeRenders(change: ActivityChange): boolean {
  if (change.target.type === "assignees") {
    return (change.users?.length ?? 0) + (change.teams?.length ?? 0) > 0;
  }
  if (change.target.type === "values") {
    return (change.fields?.length ?? 0) > 0;
  }
  return true;
}

/**
 * Process activity changeset into an array of ActivityChange objects.
 */
function processChangeset(activity: ActivityNode): ActivityChange[] {
  return Object.keys(activity.changeset ?? {}).reduce<ActivityChange[]>(
    (changes, changeSubject) => {
      const verb = lifecycleVerb(changeSubject);
      if (changeSubject === "values") {
        const addedFields: string[] = [];
        const removedFields: string[] = [];
        const editedFields: string[] = [];

        Object.keys(activity.changeset[changeSubject]).forEach((fieldId) => {
          const field = activity.changeset[changeSubject][fieldId];
          const afterField = field?.after;
          const beforeField = field?.before;
          if (
            afterField !== undefined &&
            (beforeField === undefined || beforeField === "")
          )
            addedFields.push(fieldId);
          if (afterField === undefined && beforeField !== undefined)
            removedFields.push(fieldId);
          if (afterField !== undefined && beforeField !== undefined) {
            if (!removedFields.includes(fieldId)) editedFields.push(fieldId);
          }
        });

        const fields = activity.fields ?? [];

        if (addedFields.length > 0) {
          changes.push({
            action: "added",
            target: { type: "values", label: "values" },
            fields: fields.filter((f) =>
              addedFields.includes(f.customFieldId),
            ),
          });
        }
        if (removedFields.length > 0) {
          if (!Object.keys(activity.changeset).includes("task")) {
            changes.push({
              action: "removed",
              target: { type: "values", label: "values" },
              fields: fields.filter((f) =>
                removedFields.includes(f.customFieldId),
              ),
            });
          }
        }
        if (editedFields.length > 0) {
          changes.push({
            action: "edited",
            target: { type: "values", label: "values" },
            fields: fields.filter((f) =>
              editedFields.includes(f.customFieldId),
            ),
          });
        }
        return [...changes];
      } else if (changeSubject === "note") {
        if (
          activity.changeset[changeSubject]?.before &&
          !activity.changeset[changeSubject]?.after
        ) {
          changes.push({
            action: "deleted",
            subject: "note",
            target: { type: "note", label: "Note" },
            note: activity.changeset[changeSubject],
          });
          return [...changes];
        }
      } else if (changeSubject === "noteReply") {
        let activityAction = "replied to";
        let activitySubject = "comment";

        Object.keys(activity.changeset[changeSubject]).forEach((noteId) => {
          const note = activity.changeset[changeSubject][noteId];
          if (note.after && note.before) {
            activityAction = "edited";
            activitySubject = "reply";
          }
        });

        changes.push({
          action: activityAction,
          subject: activitySubject,
          target: { type: "noteReply", label: "Note Reply" },
          noteReply: activity.changeset[changeSubject],
        });
        return [...changes];
      } else if (changeSubject === "assignees") {
        const before = activity.changeset[changeSubject].before ?? [];
        const after = activity.changeset[changeSubject].after ?? [];

        const beforeMap = new Map(
          before.map((ref: any) => [
            `${ref.entityType}:${String(ref.entityId)}`,
            ref,
          ]),
        );
        const afterMap = new Map(
          after.map((ref: any) => [
            `${ref.entityType}:${String(ref.entityId)}`,
            ref,
          ]),
        );

        const addedRefs = after.filter(
          (ref: any) =>
            !beforeMap.has(`${ref.entityType}:${String(ref.entityId)}`),
        );
        const removedRefs = before.filter(
          (ref: any) =>
            !afterMap.has(`${ref.entityType}:${String(ref.entityId)}`),
        );

        const users = activity.users ?? [];
        const teams = activity.teams ?? [];

        if (addedRefs.length > 0) {
          const addedUserIds = addedRefs
            .filter((ref: any) => ref.entityType === "user")
            .map((ref: any) => String(ref.entityId));
          const addedTeamIds = addedRefs
            .filter((ref: any) => ref.entityType === "team")
            .map((ref: any) => String(ref.entityId));

          changes.push({
            action: "added",
            target: { type: "assignees", label: "assignee" },
            users: users.filter((u) =>
              addedUserIds.includes(String(u.userId)),
            ),
            teams: teams.filter((t) =>
              addedTeamIds.includes(String(t.teamId)),
            ),
          });
        }

        if (removedRefs.length > 0) {
          const removedUserIds = removedRefs
            .filter((ref: any) => ref.entityType === "user")
            .map((ref: any) => String(ref.entityId));
          const removedTeamIds = removedRefs
            .filter((ref: any) => ref.entityType === "team")
            .map((ref: any) => String(ref.entityId));

          changes.push({
            action: "removed",
            target: { type: "assignees", label: "assignee" },
            users: users.filter((u) =>
              removedUserIds.includes(String(u.userId)),
            ),
            teams: teams.filter((t) =>
              removedTeamIds.includes(String(t.teamId)),
            ),
          });
        }
        return [...changes];
      } else if (changeSubject === "stageId") {
        return [
          ...changes,
          {
            action: "updated",
            target: { type: changeSubject, label: changeSubject },
            oldValue: {
              stage: activity.oldStage,
              workflow: activity.oldWorkflow,
            },
            newValue: {
              stage: activity.newStage,
              workflow: activity.newWorkflow,
            },
          },
        ];
      } else if (changeSubject === "age") {
        const { before, after } = activity.changeset[changeSubject];
        const statusLabel = (status: string) => {
          switch (status) {
            case AGE_STATUS.atRisk:
              return "At Risk";
            case AGE_STATUS.offTrack:
              return "Off Track";
            default:
              return "On Track";
          }
        };

        return [
          ...changes,
          {
            action: "updated",
            target: { type: changeSubject, label: changeSubject },
            oldValue: statusLabel(before?.status),
            newValue: statusLabel(after?.status),
          },
        ];
      } else if (verb) {
        // The changeset carries the issue id as `after`; the sentence needs
        // only the verb.
        changes.push({
          action: verb,
          target: { type: verb, label: verb },
          oldValue: { value: undefined },
          newValue: { value: undefined },
        });
        return [...changes];
      }

      // Default handling
      const { before, after } = activity.changeset[changeSubject] ?? {};
      let action = "";
      if (before === undefined && after !== undefined) action = "created";
      else if (before !== undefined && after === undefined) action = "deleted";
      else if (before !== undefined && after !== undefined) action = "updated";

      const target: ActivityChange["target"] = {
        type: changeSubject,
        label: changeSubject,
      };

      if (changeSubject === "task") {
        target.referenceId = activity.issueId;
      }

      const extraData: Partial<ActivityChange> = {};
      if (changeSubject === "stageId") {
        extraData.oldStage = activity.oldStage;
        extraData.newStage = activity.newStage;
        extraData.oldWorkflow = activity.oldWorkflow;
        extraData.newWorkflow = activity.newWorkflow;
      } else if (changeSubject === "issueTypeId") {
        extraData.oldType = activity.oldType;
        extraData.newType = activity.newType;
      } else if (changeSubject === "address") {
        extraData.oldAddress = activity.oldAddress;
        extraData.newAddress = activity.newAddress;
      }

      return [
        ...changes,
        {
          action,
          target,
          oldValue: { value: before },
          newValue: { value: after },
          ...extraData,
        },
      ];
    },
    [],
  );
}
